import { lazy, Suspense, useMemo, type AnchorHTMLAttributes, type ComponentType, type MouseEvent, type ReactNode } from 'react';
import { useBlocker, useLocation, useParams as useTsParams, useRouter as useTsRouter } from '@tanstack/react-router';

// Ponto único de navegação (T07 §3.4): mesmos nomes e assinaturas que as páginas já usavam no Next.

export function useRouter() {
    const r = useTsRouter();
    return useMemo(
        () => ({
            push: (href: string) => r.history.push(href),
            replace: (href: string) => r.history.replace(href),
            back: () => r.history.back(),
            refresh: () => void r.invalidate(),
        }),
        [r],
    );
}

export const usePathname = (): string => useLocation({ select: (l) => l.pathname });

export function useSearchParams(): URLSearchParams {
    const s = useLocation({ select: (l) => l.searchStr });
    return useMemo(() => new URLSearchParams(s), [s]);
}

/** Com `pendente`, sair da página (link, voltar, aba, fechar a janela) espera a pessoa confirmar no diálogo. */
export function useConfirmarSaida(pendente: boolean) {
    const b = useBlocker({ shouldBlockFn: () => pendente, enableBeforeUnload: pendente, withResolver: true });
    return { bloqueada: b.status === 'blocked', ficar: () => b.reset?.(), sair: () => b.proceed?.() };
}

export const useParams = <T extends Record<string, string> = Record<string, string>>(): T => useTsParams({ strict: false }) as T;

const navegaFora = (e: MouseEvent<HTMLAnchorElement>, href: string, target: string | undefined) =>
    e.defaultPrevented || target === '_blank' || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || /^[a-z][a-z0-9+.-]*:/i.test(href);

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

export function Link({ href, onClick, target, ...rest }: LinkProps) {
    const r = useTsRouter();
    return (
        <a
            {...rest}
            href={href}
            target={target}
            onClick={(e) => {
                onClick?.(e);
                if (navegaFora(e, href, target)) return;
                e.preventDefault();
                r.history.push(href);
            }}
        />
    );
}

type Carregado<P> = ComponentType<P> | { default: ComponentType<P> };

/** `next/dynamic` sobre `React.lazy`; `ssr` é ignorado (SPA). */
export function dynamic<P extends object>(
    loader: () => Promise<Carregado<P>>,
    opts?: { loading?: () => ReactNode; ssr?: boolean },
): ComponentType<P> {
    const Lazy = lazy(async () => {
        const m = await loader();
        return 'default' in m ? m : { default: m };
    });
    function Dinamico(props: P) {
        return (
            <Suspense fallback={opts?.loading?.() ?? null}>
                <Lazy {...props} />
            </Suspense>
        );
    }
    return Dinamico;
}
